import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Package, Scale, Printer, ChefHat, Bike, BarChart3, Settings, LogOut, UtensilsCrossed, Map, Menu as MenuIcon, X, Receipt, Home, Users, Vault } from 'lucide-react';
import { useAuth, canAccess } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import BrandLogo from '@/components/BrandLogo';
import SyncStatusBadge from '@/components/SyncStatusBadge';
import SyncHealthBadge from '@/components/SyncHealthBadge';

// Itens por role (Fase 7)
const NAV_BY_ROLE = {
  admin: [
    { path: '/admin', label: 'Painel', icon: Home },
    { path: '/balanca', label: 'Balança', icon: Scale },
    { path: '/caixa', label: 'Caixa', icon: Receipt },
    { path: '/historico-caixa', label: 'Histórico Caixa', icon: Vault },
    { path: '/cozinha', label: 'Cozinha', icon: ChefHat },
    { path: '/delivery-admin', label: 'Delivery', icon: Bike },
    { path: '/cardapio', label: 'Cardápio', icon: UtensilsCrossed },
    { path: '/logistica', label: 'Logística', icon: Map },
    { path: '/estoque', label: 'Estoque', icon: Package },
    { path: '/equipe', label: 'Equipe', icon: Users },
    { path: '/impressao', label: 'Impressão', icon: Printer },
    { path: '/relatorios', label: 'Relatórios', icon: BarChart3 },
    { path: '/config', label: 'Config', icon: Settings },
  ],
  garcom: [
    { path: '/garcom', label: 'Lançar', icon: LayoutGrid },
  ],
  caixa: [
    { path: '/caixa', label: 'Caixa', icon: Receipt },
    { path: '/historico-caixa', label: 'Histórico', icon: Vault },
    { path: '/cozinha', label: 'Cozinha', icon: ChefHat },
    { path: '/relatorios', label: 'Relatórios', icon: BarChart3 },
  ],
  entregador: [
    { path: '/entregador', label: 'Entregas', icon: Bike },
  ],
};

// Bottom nav mobile: máx 5 itens do role
function buildMobileItems(role) {
  const items = NAV_BY_ROLE[role] || [];
  return items.slice(0, 5);
}

export default function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { operator, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);

  const role = operator?.role || 'admin';
  const navItems = (NAV_BY_ROLE[role] || []).filter((i) => canAccess(role, i.path));
  const mobileItems = buildMobileItems(role);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const go = (path) => {
    navigate(path);
    setMobileOpen(false);
  };

  return (
    <>
      {/* Mobile top bar */}
      <header
        data-testid="mobile-topbar"
        className="md:hidden fixed top-0 left-0 right-0 z-40 bg-white/85 backdrop-blur-xl border-b border-zinc-200/80 h-14 flex items-center justify-between px-3"
      >
        <div className="flex-1 max-w-[160px]">
          <BrandLogo maxWidth={140} variant="topbar" />
        </div>
        <button
          data-testid="mobile-menu-btn"
          onClick={() => setMobileOpen(true)}
          className="w-10 h-10 rounded-lg flex items-center justify-center text-zinc-600 hover:bg-zinc-100 transition-colors"
          aria-label="Abrir menu"
        >
          <MenuIcon size={20} strokeWidth={1.5} />
        </button>
      </header>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          data-testid="mobile-drawer"
          className="md:hidden fixed inset-0 z-50 bg-zinc-900/40 backdrop-blur-sm"
          onClick={() => setMobileOpen(false)}
        >
          <aside
            className="absolute right-0 top-0 bottom-0 w-72 bg-white border-l border-zinc-200 flex flex-col animate-in slide-in-from-right duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 h-16 border-b border-slate-100">
              <div className="flex-1 pr-2">
                <BrandLogo maxWidth={150} variant="topbar" />
              </div>
              <button
                data-testid="mobile-drawer-close"
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 rounded-lg flex items-center justify-center text-zinc-500 hover:bg-zinc-100"
                aria-label="Fechar"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex-1 p-2 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    data-testid={`nav-${item.path.replace('/', '') || 'mesas'}`}
                    onClick={() => go(item.path)}
                    className={cn(
                      'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors',
                      active
                        ? 'bg-zinc-100 text-zinc-900'
                        : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                    )}
                  >
                    <Icon size={18} strokeWidth={1.75} />
                    {item.label}
                  </button>
                );
              })}
            </nav>
            <div className="p-3 border-t border-zinc-100">
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-zinc-500 hover:bg-red-50 hover:text-red-600 transition-colors"
              >
                <LogOut size={18} strokeWidth={1.75} />
                Sair
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        data-testid="sidebar"
        className="hidden md:flex fixed left-0 top-0 bottom-0 w-[240px] bg-slate-50 border-r border-slate-200 flex-col z-40"
      >
        <div className="px-4 pt-5 pb-4 border-b border-slate-200 flex items-center justify-center">
          <BrandLogo maxWidth={180} variant="sidebar" />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = location.pathname === item.path;
            return (
              <button
                key={item.path}
                data-testid={`nav-${item.path.replace('/', '') || 'mesas'}`}
                onClick={() => navigate(item.path)}
                className={cn(
                  'group relative w-full flex items-center gap-2.5 pl-3.5 pr-3 py-2 rounded-lg text-left text-sm font-medium transition-all duration-150 ease-out',
                  active
                    ? 'bg-blue-50 text-blue-700 before:content-[""] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-r-full before:bg-blue-600'
                    : 'text-slate-600 hover:bg-white hover:text-slate-900'
                )}
              >
                <Icon
                  size={17}
                  strokeWidth={1.5}
                  className={cn(
                    'transition-colors',
                    active ? 'text-blue-600' : 'text-slate-400 group-hover:text-slate-700'
                  )}
                />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-2 pt-1 pb-2 space-y-2">
            <SyncStatusBadge />
            <SyncHealthBadge />
          </div>
          <div className="flex items-center gap-2.5 px-2 py-2 mb-1">
            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-600 to-blue-500 flex items-center justify-center text-white text-xs font-bold">
              {operator?.name?.[0] || 'U'}
            </div>
            <span className="text-sm font-medium text-slate-700 truncate">
              {operator?.name || 'Operador'}
            </span>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left text-sm font-medium text-slate-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={17} strokeWidth={1.5} />
            Sair
          </button>
        </div>
      </aside>

      {/* Mobile bottom navigation */}
      <nav
        data-testid="bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-xl border-t border-slate-200 flex items-stretch h-16"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {mobileItems.map((item) => {
          const Icon = item.icon;
          const active = location.pathname === item.path;
          return (
            <button
              key={item.path}
              data-testid={`bottom-nav-${item.path.replace('/', '') || 'mesas'}`}
              onClick={() => navigate(item.path)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 transition-colors',
                active ? 'text-blue-600' : 'text-slate-400 hover:text-slate-700'
              )}
            >
              <Icon size={20} strokeWidth={active ? 2 : 1.5} />
              <span className="text-[10px] font-medium tracking-tight">{item.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
